import { Tab } from '@/utils/tabs';
import { DownloadOutlined, SaveOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from '@tanstack/react-query';
import { App, Badge, Button, Spin, Tabs, Tooltip } from 'antd';
import { FC, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../..//context/AuthProvider';
import { setCompanies } from '../../../contextRedux/Company/CompanyStore';
import { setDevices } from '../../../contextRedux/DeviceType/DeviceTypeStore';
import { setInterventionFiles } from '../../../contextRedux/InterventionFile/InterventionFileStore';
import { setOffer, updateEquipmentTab, updateField } from '../../../contextRedux/Offer/OfferModel';
import { setOfferStatuses } from '../../../contextRedux/StatusOffer/StatusOfferStore';
import { setSystemTypes } from '../../../contextRedux/SystemType/SystemTypeStore';
import { setUsers } from '../../../contextRedux/User/UserStore';
import CompanyService from '../../../services/company/CompanyService';
import ConfigurationService from '../../../services/configuration/ConfigurationService';
import DeviceTypeService from '../../../services/deviceType/DeviceTypeService';
import InterventionFileService from '../../../services/interventionFile/InterventionFileService';
import OfferService from '../../../services/offer/OfferService';
import SystemTypeService from '../../../services/systemType/SystemTypeService';
import UserService from '../../../services/user/UserService';
import WorkPlaceService from '../../../services/workPlace/WorkPlaceService';
import { RootState } from '../../../store';
import { saveBlobCrossPlatform } from '../../../utils/saveBlobCrossPlatform';
import AddEquipmentTab from './Tabs/AddEquipmentTab/AddEquipmentTab';
import CommentsTab from './Tabs/CommentsTab/CommentsTab';
import CompanyDetailsTab from './Tabs/CompanyDetailsTab/CompanyDetailsTab';
import LaborTab from './Tabs/LaborTab/LaborTab';
import ReviewTab from './Tabs/ReviewTab/ReviewTab';
import SummaryTab from './Tabs/SummaryTab/SummaryTab';

const NewOffer: FC = () => {
  const isMobile = useMediaQuery({ maxWidth: 720 });
  const { modal, message } = App.useApp();

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const location = useLocation();

  const useQueryParams = () => {
    return new URLSearchParams(useLocation().search);
  };

  const query = useQueryParams();
  const interventionFileIdFromRoute = query.get('interventionFileId');
  const [id, setId] = useState<string | null>(query.get('id'));

  const reduxOffer = useSelector((state: RootState) => state?.models?.offer);

  const addedDevices = useSelector((state: RootState) => state?.models?.offer?.offerWorkPlaceItems);

  const [totalOfferValue, setTotalOfferValue] = useState<number>(0);

  const isShowCompanyDetailsTab: boolean = !!(
    reduxOffer?.companyId &&
    reduxOffer?.accountManagerId &&
    reduxOffer?.accountSaleId &&
    reduxOffer?.enumOfferStatusId &&
    reduxOffer?.datePeriod &&
    reduxOffer?.acquisitionType &&
    reduxOffer?.date &&
    reduxOffer?.dateStart &&
    reduxOffer?.dateEnd
  );

  const userRols = 'sales';

  const { profile } = useAuth();

  const { data: _accountSales, isFetching: isUsersLoading } = useQuery({
    queryKey: ['users'],
    queryFn: () =>
      UserService.search({
        isActive: true,
        rols: userRols,
      }).then((data) => {
        dispatch(setUsers(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  useEffect(() => {
    const idFromUrl = query.get('id');
    if (!idFromUrl) {
    }
    setId(idFromUrl);
  }, [location.search]);

  useEffect(() => {
    if (id) {
      dispatch(updateField({ field: 'id', value: id }));
    } else {
      dispatch(updateField({ field: 'id', value: null }));
    }
  }, [id, dispatch]);

  const { data: _offer, isFetching: isOffersLoading } = useQuery({
    queryKey: ['offers'],
    queryFn: () =>
      OfferService.search({
        ids: [id!],
        isActive: true,
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isOfferWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }).then((data) => {
        dispatch(setOffer(data?.response[0]));
        return data?.response[0];
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: companies,
    isFetching: isCompaniesLoading,
    refetch: searchCompanies,
  } = useQuery({
    queryKey: ['companies'],
    queryFn: () =>
      CompanyService.search({
        isAgentIncluded: true,
        isWorkPlacesIncluded: true,
        isAccountManagerIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setCompanies(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _status, isFetching: isStatusLoading } = useQuery({
    queryKey: ['offerStatuses'],
    queryFn: () =>
      ConfigurationService.searchOfferStatus({}).then((data) => {
        dispatch(setOfferStatuses(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _systemTypes, isFetching: isSystemTypesLoading } = useQuery({
    queryKey: ['systemTypes'],
    queryFn: () =>
      SystemTypeService.search({
        isDeviceTypeIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setSystemTypes(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _deviceTypes, isFetching: isDeviceTypesLoading } = useQuery({
    queryKey: ['deviceTypes'],
    queryFn: () =>
      DeviceTypeService.search({
        isSystemTypeIncluded: true,
        isLaborIncluded: true,
        isActive: true,
      }).then((data) => {
        dispatch(setDevices(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const {
    data: _offerComments,
    refetch: searchComments,
    isFetching: isOfferCommentsLoading,
  } = useQuery({
    queryKey: ['offerComment'],
    queryFn: () =>
      OfferService.searchComment({
        ids: [id!],
      }).then((data) => {
        const departments = ['general', 'contabilitate', 'manager', 'instalatori'];

        // Initialize offerComments with all departments
        const initialOfferComments = departments.map((departmentName) => {
          const existingComment = data?.response?.find((comment: any) => comment.departmentName === departmentName);
          return (
            existingComment || {
              id: '',
              offerId: id,
              departmentName,
              message: '',
              offerCommentFiles: [],
            }
          );
        });
        dispatch(updateField({ field: 'offerCommentsTab', value: initialOfferComments }));
        return data?.response;
      }),
    enabled: !!id,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: _interventionFile, isFetching: isInterventionFilesLoading } = useQuery({
    queryKey: ['interventionFiles'],
    queryFn: () =>
      InterventionFileService.search({
        ids: [interventionFileIdFromRoute!],
        isInterventionFileSystemTypeIncluded: true,
        isInterventionFileInvoiceFilesIncluded: true,
        isInterventionFileEngineerIncluded: true,
        isInterventionFilePerformOperationsIncluded: true,
        isInterventionFileEquipmentsIncluded: true,
        isMentenancePlanningIncluded: true,
        isCompanyIncluded: true,
        isWorkPlaceIncluded: true,
        isAgentIncluded: true,
        isStatusIncluded: true,
        hasRecommendationDetails: true,
      }).then((data) => {
        setInterventionFiles(data?.response[0]);
        return data?.response[0];
      }),
    enabled: !!interventionFileIdFromRoute,
    refetchOnWindowFocus: false,
  });

  const { mutate: saveOffer, isPending: isSavingOffer } = useMutation({
    mutationFn: (formData: any) => OfferService.upsert(id, formData),
    onSuccess: (data: any) => {
      if (data) {
        handleResponse(data);
        if (!!interventionFileIdFromRoute) {
          //status ofertatata
          let interventionFile = _interventionFile;
          interventionFile.changes = [
            ...(interventionFile.changes || []),
            `[${profile?.name}] In Ofertare ${new Date().toLocaleString()}`,
          ];

          saveInterventionFile({ ...interventionFile, statusId: 5 });
        }
        data?.response && navigate(`/service-maintenance/offer/edit?id=${data?.response?.id}`);
        if (!!id) {
          searchComments();
        }
      } else {
        console.error('Received data is not in JSON format or JSON parsing failed.');
      }
    },
  });

  const { mutate: updateWorkPace } = useMutation({
    mutationFn: (formData: any) => WorkPlaceService.upsert(formData.id, formData),
    onSuccess: () => {},
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const { mutate: saveInterventionFile, isPending: isSavingInterventionFile } = useMutation({
    mutationFn: (formData: any) => InterventionFileService.upsert(formData.id, formData),
    onSuccess: (data: any) => {
      if (!data) {
        console.error('Received data is not in JSON format or JSON parsing failed.');
        return;
      }
    },
    onError: (error: any) => {
      message.error(`Error processing your request: ${error.message}`);
    },
  });

  const onSaveOffer = async () => {
    saveOffer({ ...reduxOffer, offerComments: reduxOffer?.offerCommentsTab });
  };

  const filterAndGroupData = (data: any) => {
    const result: any = {};

    data.forEach((item: any) => {
      const { workPlace, workPlaceId, systemTypeId, deviceTypeId, piece, isAnotherProvider, details } = item;

      if (!result[workPlaceId]) {
        result[workPlaceId] = {
          workPlace: workPlace,
          workPlaceId: workPlaceId,
          devices: [],
        };
      }

      result[workPlaceId].devices.push({
        systemTypeId,
        deviceTypeId,
        piece,
        isAnotherProvider,
        details,
      });
    });

    return Object.values(result);
  };

  const handleSaveOffer = async () => {
    try {
      modal.confirm({
        title: 'Confirm Update',
        content: 'Sigur doriți să actualizați punctele de lucru cu configurația curentă?',
        okText: 'Da',
        cancelText: 'Nu',
        onOk: async () => {
          const workPlaceItems = filterAndGroupData(addedDevices);
          workPlaceItems.forEach((workPlaceItem: any) => {
            const formData = {
              ...workPlaceItem?.workPlace,
              agents:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem.workPlace.id)?.agents ?? [],
              contactPersons:
                companies
                  .find((c: any) => c.id == workPlaceItem?.workPlace?.companyId)
                  .workPlaces.find((wp: any) => wp.id == workPlaceItem?.workPlace?.id)?.contactPersons ?? [],
              workPlaceItems: workPlaceItem.devices.map((d: any) => {
                return {
                  id: '',
                  workPlaceId: workPlaceItem.workPlaceId,
                  deviceTypeId: d.deviceTypeId,
                  systemTypeId: d.systemTypeId,
                  details: d.details ?? '',
                  piece: d.piece,
                  metadata: '{}',
                  isAnotherProvider: d.isAnotherProvider ?? false,
                };
              }),
            };
            updateWorkPace(formData);
          });
          await onSaveOffer();
        },
        onCancel: async () => {
          await onSaveOffer();
        },
      });
    } catch (errorInfo: any) {
      message.error(errorInfo?.errorFields[0]?.errors);
    }
  };

  const handleResponse = (data: any) => {
    if (data?.errors?.length > 0) {
      data?.errors?.forEach((item: any) => {
        message.error(item);
      });
    } else {
      message.success(
        <span>
          <span className='font-weight-bold'>{data?.response?.number}</span> a fost {!!id ? 'modificat' : 'adaugat'} cu
          success
        </span>
      );
    }
  };

  const { mutate: viewPdfPmOffer, isPending: isViewingPdfOffer } = useMutation({
    mutationFn: (formData: any) => OfferService.preview(id, formData),
    onSuccess: (data: any) => {
      if (!data || !data.blob) {
        throw new Error('Invalid response received from the server');
      }
      return data.blob().then((blob: any) => {
        const url = window.URL.createObjectURL(blob);
        dispatch(
          updateEquipmentTab({
            field: 'pdfUrl',
            value: url,
          })
        );
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  const viewPdf = () => {
    if (isShowCompanyDetailsTab) {
      viewPdfPmOffer(reduxOffer);
    }
  };

  const { mutate: printOfferToPdf, isPending: isPrintinOffer } = useMutation({
    mutationFn: () =>
      OfferService.print({
        ids: [id!],
        isStatusIncluded: true,
        isCompanyIncluded: true,
        isOfferWorkPlaceIncluded: true,
        isAccountManagerIncluded: true,
        isAccountSaleIncluded: true,
      }),
    onSuccess: (response: any) => {
      // Extract filename from Content-Disposition header
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = 'file.pdf'; // Default filename if header is missing

      if (contentDisposition) {
        const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
        const matches = filenameRegex.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      return response.blob().then((blob: any) => {
        saveBlobCrossPlatform(blob, filename);
      });
    },
    onError: () => {
      message.error('Eroare procesare request.');
    },
  });

  useEffect(() => {
    if (addedDevices) {
      const totalLabor = Number.parseFloat(
        addedDevices
          .reduce(
            (total, item) =>
              total +
              item.piece *
                item.laborPrice *
                (item.period === 'lunar'
                  ? 12
                  : item.period === 'trimestrial'
                  ? 4
                  : item.period === 'semestrial'
                  ? 2
                  : item.period === 'anual'
                  ? 1
                  : 1),
            0
          )
          .toFixed(2)
      );

      if (addedDevices.some((ad) => ad.period === 'la cerere')) {
        const groupedDevices = addedDevices.reduce((acc, device) => {
          const key = `${device.systemType.name}-${device.unitMeasure}-${device.period}-${device.workPlaceId}`;

          if (!acc[key]) {
            acc[key] = {
              systemType: device.systemType.name,
              periodicity: device.period,
              unitMeasure: device.unitMeasure,
              interventionPriceOnRequest: 0,
              totalPrice: 0,
              workPlaceId: device.workPlaceId,
              count: 0,
            };
          }

          acc[key].interventionPriceOnRequest += device.interventionPriceOnRequest;
          acc[key].totalPrice += device.laborPrice;
          acc[key].count += 1;

          return acc;
        }, {});

        // Calculate the averages and transform into an array
        const result = Object.values(groupedDevices).map((group: any) => ({
          ...group,
          interventionPriceOnRequest: group.interventionPriceOnRequest / group.count,
        }));

        const totalInterventionPriceOnRequest = result.reduce(
          (acc, group) => acc + group.interventionPriceOnRequest,
          0
        );
        setTotalOfferValue(totalInterventionPriceOnRequest + (reduxOffer?.isExpedite == true ? 250 : 0));
      } else {
        setTotalOfferValue(totalLabor + (reduxOffer?.isExpedite == true ? 250 : 0));
      }
    }
  }, [addedDevices, reduxOffer?.isExpedite]);

  const tabs: (Tab | false)[] = [
    {
      key: 'companyTab',
      label: 'Detalii Client',
      children: <CompanyDetailsTab />,
    },
    isShowCompanyDetailsTab && {
      key: 'deviceTab',
      label: 'Adaugare Echipament',
      children: <AddEquipmentTab searchCompanies={searchCompanies} />,
    },
    addedDevices?.length! > 0 && {
      key: 'summaryTab',
      label: (
        <>
          Vizualizare Echipamente Adaugate
          <Badge
            color='blue'
            style={{ marginLeft: '5px' }}
            count={addedDevices?.length! > 0 ? addedDevices?.length : ''}
          ></Badge>
        </>
      ),
      children: <SummaryTab />,
    },
    addedDevices?.length! > 0 &&
      addedDevices.some((ad) => ad.period !== 'la cerere') && {
        key: 'manoperaTab',
        label: 'Manopera',
        children: <LaborTab />,
      },
    addedDevices?.length! > 0 && {
      key: 'commentsTab',
      label: 'Observatii si Comentarii',
      children: <CommentsTab />,
    },
    addedDevices?.length! > 0 &&
      isShowCompanyDetailsTab && {
        key: 'reviewTab',
        label: 'Review',
        children: <ReviewTab isViewingPdfOffer={isViewingPdfOffer} />, // Use key to force re-render
      },
  ];

  const filteredTabs: Tab[] = tabs.filter((tab): tab is Tab => tab !== false);

  if (
    isSavingOffer ||
    isPrintinOffer ||
    isOffersLoading ||
    isCompaniesLoading ||
    isStatusLoading ||
    isSystemTypesLoading ||
    isDeviceTypesLoading ||
    isOfferCommentsLoading ||
    isUsersLoading ||
    isSavingInterventionFile ||
    isInterventionFilesLoading
  ) {
    return <Spin size='large' className='spinner' />;
  }

  return (
    <>
      <div className='topLine'>
        <h2>{id ? 'Oferta Mentenanta - ' + reduxOffer?.number : 'Oferta Noua Mentenanta'}</h2>
        <div className='totalLine'>
          <h3>
            {reduxOffer?.isExpedite ? (
              <span style={{ color: 'red' }}>Urgent Total - {totalOfferValue}</span>
            ) : (
              'Total - ' + totalOfferValue
            )}
          </h3>{' '}
          <div className='buttons'>
            <Button onClick={() => handleSaveOffer()} className='saveBtn'>
              {isMobile ? (
                <Tooltip title='Salveaza Oferta'>
                  <SaveOutlined />
                </Tooltip>
              ) : (
                'Salveaza Oferta'
              )}
            </Button>

            {addedDevices?.length! > 0 && (
              <Button className='downloadBtn' onClick={() => printOfferToPdf()}>
                {isMobile ? (
                  <Tooltip title='Descărcare PDF'>
                    <DownloadOutlined />
                  </Tooltip>
                ) : (
                  <>
                    {' '}
                    <DownloadOutlined /> Descărcare PDF{' '}
                  </>
                )}
              </Button>
            )}
          </div>
        </div>
      </div>

      <Tabs
        className='tabs'
        defaultActiveKey='1'
        items={filteredTabs}
        onChange={(key) => {
          if (key === 'reviewTab') {
            viewPdf();
          }
        }}
        tabBarStyle={{ backgroundColor: '#DADBDD' }}
        tabBarGutter={18}
      />
    </>
  );
};

export default NewOffer;
